import React from 'react';
import { Div, Span } from '@kizi/core-components';
import { Checkbox } from '@kizi/shared-components';
import { withFormProps } from '@kizi/comparator-components';
import { useField, usePage, useWizardService } from '@kizi/comparator-core';
import { SummaryCostInfo } from '../wrappers/ProductSummaryWrapper';
import { transformCost, CostInfo } from '../../utils/extraOptions';
import { GetComposedForm_composedForm_questions as Question } from '@kizi/comparator-core';

interface ExtraOptionsCheckboxProps {
  label: string;
  question: Question;
  name: string;
}

const SpanWithTooltip = withFormProps(Span);
const FormCheckbox = withFormProps(Checkbox, {
  _containerProps: {
    variant: 'formGroupInlineReversed',
    alignContent: 'center',
  },
});

export const ExtraOptionsCheckbox: React.FC<ExtraOptionsCheckboxProps> = ({
  label: label,
  question,
  name,
}) => {
  if (!question) return null;
  const field = useField(name);
  const { setState, state } = usePage();
  const service = useWizardService();
  const handleChange = () => {
    field?.set({ value: field?.value === 'true' ? 'false' : 'true' });
  };
  const addOptionToProductSummary = (
    cost: CostInfo,
    costType: 'monthly' | 'onceOff',
  ) => {
    if (!state || !setState) return;
    const data = (state[costType] || []) as SummaryCostInfo[];
    const index = data.findIndex(
      (option) => option.fieldName === name && option.name === cost.name,
    );
    if (index > -1) {
      data.splice(index, 1);
    } else {
      data.push({
        name: cost.name,
        rates: cost[costType].items,
        fieldName: name,
      });
    }
    setState({ ...state, [costType]: data });
  };

  const handleRetention = async (val: string) => {
    const data = service?.getSnapshot();
    const isActive =
      field?.value === undefined || !(field?.value as string).includes(val);
    if (data && setState) {
      service?.send([
        {
          type: 'SET_FORM_FIELDS',
          name: 'samenstelling',
          fields: [
            ...data.context.initialSteps[0].fields
              .filter((f) => f.name !== name)
              .map((f) => ({
                ...f,
                active: !isActive,
              })),
          ],
        },
        {
          type: 'ADD_FIELDS',
          fields: [
            {
              formName: 'samenstelling',
              name: 'canContinue',
              value: isActive ? 0 : 1,
            },
          ],
        },
      ]);
      setState({ ...state, retention: isActive });
    }
  };

  const handleCustomerBenefit = (description: string) => {
    if (description && setState) {
      const currentBenefitInfo: string[] =
        (state?.benefitInfo as string[]) || [];
      const index = currentBenefitInfo.findIndex(
        (item) => item === description,
      );
      if (index > -1) {
        currentBenefitInfo.splice(index, 1);
      } else {
        currentBenefitInfo.push(description);
      }

      setState({ ...state, benefitInfo: currentBenefitInfo });
    }
  };

  return (
    <Div variant="checkboxGroup" mb={2}>
      <Div>
        <Div variant="extraOptionsCheckbox">
          <FormCheckbox
            _tooltip={!question.custom?.cost ? question.description : ''}
            label={label}
            onChange={() => {
              handleChange();
              handleCustomerBenefit(question.custom?.disclaimer || '');
              question.custom?.cost &&
                addOptionToProductSummary(
                  question.custom?.cost,
                  question.custom?.cost?.monthly ? 'monthly' : 'onceOff',
                );
              handleRetention(question.value);
            }}
            checked={field?.value === 'true'}
            value={field?.value}
            labelPosition="right"
            variant="main"
          />
          {question.custom?.cost?.monthly && (
            <SpanWithTooltip
              _tooltip={question.description || ''}
              _containerProps={{ flexDirection: 'row-reverse' }}
            >
              {transformCost(question.custom?.cost)}
            </SpanWithTooltip>
          )}
        </Div>
      </Div>
    </Div>
  );
};
